import Phaser from 'phaser';
import { GAME_WIDTH } from '@/config/gameConfig';
import { THEME } from '@/config/theme';
import { drawArcadeBackground, addTextGlow } from '@/utils/arcadeBackground';
import { loadProgress, getSelectedDifficulty, setSelectedDifficulty } from '@/config/progress';
import { getActiveMessageSlots, getMessageText, type MessageEntry } from '@/config/messages';
import { isMobileDevice } from '@/utils/mobile';
import { mountAccountBadge } from '@/ui/accountBadge';
import { setDiscordLevelSelectPresence } from '@/discord';
import { createBackButton, createButton } from '@/ui/buttons';
import { AudioManager } from '@/systems/AudioManager';
import { trackLevelSelectView } from '@/services/analytics';

const LEVEL_SELECT_BUTTON_SIZES = {
  primary: { width: 200, height: 50, fontSize: THEME.sizes.bodyLarge },
};

interface LevelSelectInit {
  levelIndex?: number;
}

export class LevelSelectScene extends Phaser.Scene {
  private selectedLevel: number = 0;
  private selectedDifficulty: string = 'easy';
  private levelBackgrounds: Phaser.GameObjects.Rectangle[] = [];
  private difficultyButtons: Phaser.GameObjects.Rectangle[] = [];
  private difficultyTexts: Phaser.GameObjects.Text[] = [];
  private initData: LevelSelectInit | undefined;
  private listContainer!: Phaser.GameObjects.Container;
  private scrollY: number = 0;
  private maxScroll: number = 0;
  private scrollbarThumb!: Phaser.GameObjects.Rectangle;
  private scrollbarTrack!: Phaser.GameObjects.Rectangle;
  private listHeight: number = 0;
  private readonly LIST_RIGHT_EDGE_X = GAME_WIDTH / 2 + 310;
  private progressSnapshot = loadProgress();
  private activeSlots: MessageEntry[] = getActiveMessageSlots();

  constructor() {
    super({ key: 'LevelSelectScene' });
  }

  init(data?: LevelSelectInit): void {
    this.initData = data;

    // Always read difficulty from localStorage (source of truth)
    this.selectedDifficulty = getSelectedDifficulty();
    this.scrollY = 0;
    this.progressSnapshot = loadProgress();
    this.activeSlots = getActiveMessageSlots();

    // Level can be passed in (e.g., from ResultScene for "Play Next")
    this.selectedLevel = this.initData?.levelIndex ?? (this.activeSlots[0]?.slot ?? 0);

    // Ensure selected level is unlocked, otherwise select first unlocked
    if (!this.isUnlocked(this.selectedLevel)) {
      const firstUnlocked = this.activeSlots.find((entry) => this.isUnlocked(entry.slot));
      this.selectedLevel = firstUnlocked?.slot ?? (this.activeSlots[0]?.slot ?? 0);
    }
  }

  create(): void {
    if (isMobileDevice()) {
      this.scene.start('JoinScene', { notice: 'This mode requires desktop. Join via invite link or room code.' });
      return;
    }

    // Track level select view
    trackLevelSelectView();

    // Discord Activity presence (level select)
    setDiscordLevelSelectPresence();

    this.cameras.main.fadeIn(300, 5, 7, 22);
    drawArcadeBackground(this);
    mountAccountBadge(this);

    // Layout constants
    const panelTop = 200;
    const panelHeight = 300;
    const panelBottom = panelTop + panelHeight;

    // Panel frame - contains only the level list
    const frame = this.add.rectangle(GAME_WIDTH / 2, panelTop + panelHeight / 2, 760, panelHeight, THEME.colors.backgroundLightHex, 0.82);
    frame.setStrokeStyle(3, THEME.colors.magentaHex);
    frame.setDepth(-1);

    // Title - ABOVE the purple panel
    const title = this.add.text(GAME_WIDTH / 2, 100, 'Select a message, difficulty,\nand click Play', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.title,
      color: THEME.colors.cyan,
      align: 'center',
    }).setOrigin(0.5);
    addTextGlow(title, THEME.colors.cyan, 15);

    // Column headers - inside panel near top
    this.add.text(350, panelTop + 25, 'MESSAGE', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.small,
      color: THEME.colors.textMuted,
    }).setOrigin(0, 0.5);

    this.add.text(this.LIST_RIGHT_EDGE_X - 10, panelTop + 25, 'HIGH SCORE', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.small,
      color: THEME.colors.textMuted,
    }).setOrigin(1, 0.5);

    // Scrollable list area - inside panel
    const listTop = panelTop + 50;
    this.listHeight = panelHeight - 60;

    // Mask for the list
    const maskShape = this.add.rectangle(GAME_WIDTH / 2, listTop + this.listHeight / 2, 620, this.listHeight, 0xffffff);
    maskShape.setVisible(false);
    const mask = maskShape.createGeometryMask();

    this.listContainer = this.add.container(0, 0);
    this.listContainer.setMask(mask);

    this.createLevelList(listTop);

    // Calculate max scroll
    const rowSpacing = 55;
    const totalHeight = this.activeSlots.length * rowSpacing;
    this.maxScroll = Math.max(0, totalHeight - this.listHeight);

    this.createScrollbar(listTop, this.listHeight);

    // Scroll input
    this.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gameObjects: Phaser.GameObjects.GameObject[], _deltaX: number, deltaY: number) => {
      this.scrollY = Phaser.Math.Clamp(this.scrollY + deltaY * 0.5, 0, this.maxScroll);
      this.listContainer.y = -this.scrollY;
      this.updateScrollbar();
    });

    // Difficulty section - positioned below the panel with equal spacing to play button
    const difficultyY = panelBottom + 30;
    this.add.text(GAME_WIDTH / 2, difficultyY, 'DIFFICULTY', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.small,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);

    this.createDifficultySelector(difficultyY + 35);

    // Play button - below difficulty with equal spacing
    createButton(this, {
      x: GAME_WIDTH / 2,
      y: difficultyY + 105,
      text: 'PLAY',
      buttonType: 'normal',
      onClick: () => {
        this.transitionTo('SingleGameScene', {
          levelIndex: this.selectedLevel,
          message: getMessageText(this.selectedLevel),
          difficulty: this.selectedDifficulty,
        });
      },
      style: 'primary',
      sizes: LEVEL_SELECT_BUTTON_SIZES,
    });

    // Back button
    createBackButton(this, {
      x: 100,
      y: 60,
      onClick: () => {
        this.transitionTo('MenuScene');
      },
    });
  }

  private createLevelList(startY: number): void {
    const rowSpacing = 55;
    const rowHeight = 50;
    this.levelBackgrounds = [];

    const messageX = 350;
    const scoreRightX = this.LIST_RIGHT_EDGE_X - 60;
    const gradeCenterX = this.LIST_RIGHT_EDGE_X - 25;
    const messageMaxWidth = (scoreRightX - 24) - messageX;

    this.activeSlots.forEach((entry, listIndex) => {
      const y = startY + listIndex * rowSpacing + rowHeight / 2;
      const slot = entry.slot;
      const message = entry.text;

      const unlocked = this.isUnlocked(slot);
      const highScore = this.getHighScore(slot);

      const bgColor = unlocked ? THEME.colors.backgroundLightHex : 0x1a1a2e;
      const bg = this.add.rectangle(GAME_WIDTH / 2, y, 620, rowHeight, bgColor, 1);
      const stroke = slot === this.selectedLevel && unlocked ? THEME.colors.cyanHex : THEME.colors.borderHex;
      bg.setStrokeStyle(2, stroke);
      this.levelBackgrounds.push(bg);

      this.listContainer.add(bg);

      if (unlocked) {
        const titleText = this.add.text(messageX, y, message, {
          fontFamily: THEME.fonts.body,
          fontSize: THEME.sizes.bodyLarge,
          color: THEME.colors.text,
        }).setOrigin(0, 0.5);
        this.truncateTextToFit(titleText, messageMaxWidth);
        this.listContainer.add(titleText);

        if (highScore > 0) {
          const grade = this.getHighGrade(slot);
          const scoreText = this.add.text(scoreRightX, y, highScore.toLocaleString(), {
            fontFamily: THEME.fonts.heading,
            fontSize: THEME.sizes.subheading,
            color: THEME.colors.yellow,
          }).setOrigin(1, 0.5);
          this.listContainer.add(scoreText);

          if (grade) {
            const { fillHex, textColor } = this.getGradeStyle(grade);
            const badgeWidth = grade.length >= 2 ? 44 : 34;
            const badgeHeight = 26;

            const badge = this.add.rectangle(gradeCenterX, y, badgeWidth, badgeHeight, fillHex, 0.22);
            badge.setStrokeStyle(2, fillHex, 0.85);
            this.listContainer.add(badge);

            const gradeText = this.add.text(gradeCenterX, y, grade, {
              fontFamily: THEME.fonts.heading,
              fontSize: THEME.sizes.bodyLarge,
              color: textColor,
            }).setOrigin(0.5, 0.5);
            this.listContainer.add(gradeText);
          } else {
            const gradeText = this.add.text(gradeCenterX, y, '—', {
              fontFamily: THEME.fonts.heading,
              fontSize: THEME.sizes.subheading,
              color: THEME.colors.textMuted,
            }).setOrigin(0.5, 0.5);
            this.listContainer.add(gradeText);
          }
        }

        bg.setInteractive({ useHandCursor: true });
        bg.on('pointerdown', () => {
          AudioManager.get(this).playSfx('click_option');
          this.selectedLevel = slot;
          this.updateLevelSelection();
        });
      } else {
        const lockedText = this.add.text(messageX, y, 'LOCKED', {
          fontFamily: THEME.fonts.heading,
          fontSize: THEME.sizes.body,
          color: THEME.colors.textMuted,
        }).setOrigin(0, 0.5);
        this.listContainer.add(lockedText);
      }
    });
  }

  private truncateTextToFit(text: Phaser.GameObjects.Text, maxWidth: number): void {
    if (maxWidth <= 0) return;
    if (text.width <= maxWidth) return;

    const original = text.text ?? '';
    const ellipsis = '…';
    if (!original) return;

    let low = 0;
    let high = original.length;
    let best = 0;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      text.setText(original.slice(0, mid) + ellipsis);
      if (text.width <= maxWidth) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    text.setText(original.slice(0, best) + ellipsis);
  }

  private getGradeStyle(grade: string): { fillHex: number; textColor: string } {
    switch (grade) {
      case 'SS':
      case 'S':
        return { fillHex: THEME.colors.yellowHex, textColor: THEME.colors.yellow };
      case 'A':
        return { fillHex: THEME.colors.greenHex, textColor: THEME.colors.green };
      case 'B':
        return { fillHex: THEME.colors.cyanHex, textColor: THEME.colors.cyan };
      case 'C':
        return { fillHex: THEME.colors.magentaHex, textColor: THEME.colors.magenta };
      default:
        return { fillHex: THEME.colors.borderHex, textColor: THEME.colors.textMuted };
    }
  }

  private updateLevelSelection(): void {
    this.levelBackgrounds.forEach((bg, index) => {
      const slot = this.activeSlots[index]?.slot;
      if (slot === undefined) return;
      const unlocked = this.isUnlocked(slot);
      const isSelected = slot === this.selectedLevel && unlocked;
      bg.setStrokeStyle(2, isSelected ? THEME.colors.cyanHex : THEME.colors.borderHex);
    });
  }

  private isUnlocked(index: number): boolean {
    const slotIndex = this.activeSlots.findIndex((entry) => entry.slot === index);
    if (slotIndex <= 0) return slotIndex === 0;
    const prevSlot = this.activeSlots[slotIndex - 1]?.slot;
    return prevSlot !== undefined && Boolean(this.progressSnapshot.levels[prevSlot]?.completedAt);
  }

  private getHighScore(index: number): number {
    return this.progressSnapshot.levels[index]?.highScore ?? 0;
  }

  private getHighGrade(index: number): string {
    return this.progressSnapshot.levels[index]?.highGrade ?? '';
  }

  private createScrollbar(listTop: number, listHeight: number): void {
    const scrollbarX = GAME_WIDTH / 2 + 320;
    const buttonSize = 20;
    const trackHeight = listHeight - buttonSize * 2 - 10;
    const trackY = listTop + listHeight / 2;

    // Up button
    const upBtn = this.add.rectangle(scrollbarX, listTop + buttonSize / 2 + 5, buttonSize, buttonSize, THEME.colors.backgroundPanelHex, 0.8);
    upBtn.setStrokeStyle(1, THEME.colors.borderHex);
    const upArrow = this.add.text(scrollbarX, listTop + buttonSize / 2 + 5, '▲', {
      fontFamily: THEME.fonts.body,
      fontSize: '12px',
      color: THEME.colors.cyan,
    }).setOrigin(0.5);
    upBtn.setInteractive({ useHandCursor: true });
    upBtn.on('pointerdown', () => {
      this.scrollY = Phaser.Math.Clamp(this.scrollY - 55, 0, this.maxScroll);
      this.listContainer.y = -this.scrollY;
      this.updateScrollbar();
    });
    upBtn.on('pointerover', () => upArrow.setColor(THEME.colors.text));
    upBtn.on('pointerout', () => upArrow.setColor(THEME.colors.cyan));

    // Down button
    const downBtn = this.add.rectangle(scrollbarX, listTop + listHeight - buttonSize / 2 - 5, buttonSize, buttonSize, THEME.colors.backgroundPanelHex, 0.8);
    downBtn.setStrokeStyle(1, THEME.colors.borderHex);
    const downArrow = this.add.text(scrollbarX, listTop + listHeight - buttonSize / 2 - 5, '▼', {
      fontFamily: THEME.fonts.body,
      fontSize: '12px',
      color: THEME.colors.cyan,
    }).setOrigin(0.5);
    downBtn.setInteractive({ useHandCursor: true });
    downBtn.on('pointerdown', () => {
      this.scrollY = Phaser.Math.Clamp(this.scrollY + 55, 0, this.maxScroll);
      this.listContainer.y = -this.scrollY;
      this.updateScrollbar();
    });
    downBtn.on('pointerover', () => downArrow.setColor(THEME.colors.text));
    downBtn.on('pointerout', () => downArrow.setColor(THEME.colors.cyan));

    // Scrollbar track (background)
    this.scrollbarTrack = this.add.rectangle(scrollbarX, trackY, 8, trackHeight, THEME.colors.backgroundPanelHex, 0.5);
    this.scrollbarTrack.setStrokeStyle(1, THEME.colors.borderHex);

    // Calculate thumb size based on visible ratio
    const rowSpacing = 55;
    const totalHeight = this.activeSlots.length * rowSpacing;
    const visibleRatio = Math.min(1, listHeight / totalHeight);
    const thumbHeight = Math.max(30, trackHeight * visibleRatio);

    // Scrollbar thumb
    this.scrollbarThumb = this.add.rectangle(scrollbarX, trackY - trackHeight / 2 + thumbHeight / 2, 8, thumbHeight, THEME.colors.cyanHex, 0.8);

    // Make thumb draggable
    this.scrollbarThumb.setInteractive({ useHandCursor: true, draggable: true });

    const minThumbY = trackY - trackHeight / 2 + thumbHeight / 2;
    const maxThumbY = trackY + trackHeight / 2 - thumbHeight / 2;

    this.input.on('drag', (_pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.GameObject, _dragX: number, dragY: number) => {
      if (gameObject === this.scrollbarThumb) {
        const clampedY = Phaser.Math.Clamp(dragY, minThumbY, maxThumbY);
        this.scrollbarThumb.y = clampedY;

        // Calculate scroll position from thumb position
        const thumbProgress = (clampedY - minThumbY) / (maxThumbY - minThumbY);
        this.scrollY = thumbProgress * this.maxScroll;
        this.listContainer.y = -this.scrollY;
      }
    });
  }

  private updateScrollbar(): void {
    const trackHeight = this.listHeight - 20;
    const rowSpacing = 55;
    const totalHeight = this.activeSlots.length * rowSpacing;
    const visibleRatio = Math.min(1, this.listHeight / totalHeight);
    const thumbHeight = Math.max(30, trackHeight * visibleRatio);

    const minThumbY = this.scrollbarTrack.y - trackHeight / 2 + thumbHeight / 2;
    const maxThumbY = this.scrollbarTrack.y + trackHeight / 2 - thumbHeight / 2;

    const scrollProgress = this.scrollY / this.maxScroll;
    this.scrollbarThumb.y = minThumbY + scrollProgress * (maxThumbY - minThumbY);
  }

  private createDifficultySelector(y: number): void {
    const difficulties = ['easy', 'medium', 'hard', 'expert'];
    const startX = GAME_WIDTH / 2 - 180;
    this.difficultyButtons = [];
    this.difficultyTexts = [];

    difficulties.forEach((diff, index) => {
      const x = startX + index * 120;
      const isSelected = diff === this.selectedDifficulty;

      const btn = this.add.rectangle(x, y, 100, 40, THEME.colors.backgroundPanelHex, 1);
      btn.setStrokeStyle(2, isSelected ? THEME.colors.cyanHex : THEME.colors.borderHex);
      this.difficultyButtons.push(btn);

      const text = this.add.text(x, y, diff.toUpperCase(), {
        fontFamily: THEME.fonts.heading,
        fontSize: THEME.sizes.tiny,
        color: isSelected ? THEME.colors.text : THEME.colors.textMuted,
      }).setOrigin(0.5);
      this.difficultyTexts.push(text);

      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => {
        AudioManager.get(this).playSfx('click_option');
        this.selectedDifficulty = diff;
        setSelectedDifficulty(diff);
        this.updateDifficultySelection();
      });
    });
  }

  private updateDifficultySelection(): void {
    const difficulties = ['easy', 'medium', 'hard', 'expert'];
    this.difficultyButtons.forEach((btn, index) => {
      const isSelected = difficulties[index] === this.selectedDifficulty;
      btn.setStrokeStyle(2, isSelected ? THEME.colors.cyanHex : THEME.colors.borderHex);
      this.difficultyTexts[index].setColor(isSelected ? THEME.colors.text : THEME.colors.textMuted);
    });
  }

  private transitionTo(scene: string, data?: object): void {
    this.cameras.main.fadeOut(200, 5, 7, 22);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(scene, data);
    });
  }
}
